"use server";

import {
  TGenericResponse,
  TResponseServerAction,
  TRole,
  TWorkArrangement,
  TVideoType,
  TVideoStatus,
  TEmploymentType,
  TAppointmentStatus,
  TPosition,
  TCountry,
  TLanguage,
  TCompanySize,
  TCurrency,
  TMessageType,
} from "./types";
import { api, ErrorResponseData, type AxiosError } from "@/lib/axios";
import { getCookieByName } from "@/lib/cookies";

export const getCountries = async (): Promise<
  TResponseServerAction<TCountry[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TCountry[]>>("/Countries");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Countries fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Countries fetch failed"
          : "Countries fetch failed",
    };
  }
};

export const getLanguages = async (): Promise<
  TResponseServerAction<TLanguage[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TLanguage[]>>("/Languages");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Languages fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Languages fetch failed"
          : "Languages fetch failed",
    };
  }
};

export const searchLanguages = async (
  search: string,
): Promise<TResponseServerAction<string[]>> => {
  try {
    const response = await api.get<TGenericResponse<string[]>>(
      "/language/search",
      {
        params: { search },
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    return {
      status: "success",
      message: "Languages searched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Language search failed"
          : "Language search failed",
    };
  }
};

export const getRoles = async (): Promise<TResponseServerAction<TRole[]>> => {
  try {
    const response = await api.get<TGenericResponse<TRole[]>>(
      "/Dropdown/Roles",
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Roles fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Roles fetch failed"
          : "Roles fetch failed",
    };
  }
};

export const getWorkArrangements = async (): Promise<
  TResponseServerAction<TWorkArrangement[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TWorkArrangement[]>>(
      "/Dropdown/WorkArrangements",
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Work arrangements fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Work arrangements fetch failed"
          : "Work arrangements fetch failed",
    };
  }
};

export const getVideoTypes = async (): Promise<
  TResponseServerAction<TVideoType[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TVideoType[]>>(
      "/Dropdown/VideoTypes",
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Video types fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Video types fetch failed"
          : "Video types fetch failed",
    };
  }
};

export const getVideoStatuses = async (): Promise<
  TResponseServerAction<TVideoStatus[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TVideoStatus[]>>(
      "/Dropdown/VideoStatuses",
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Video statuses fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Video statuses fetch failed"
          : "Video statuses fetch failed",
    };
  }
};

export const getEmploymentTypes = async (): Promise<
  TResponseServerAction<TEmploymentType[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TEmploymentType[]>>(
      "/Dropdown/EmploymentTypes",
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Employment types fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Employment types fetch failed"
          : "Employment types fetch failed",
    };
  }
};

export const getAppointmentStatuses = async (): Promise<
  TResponseServerAction<TAppointmentStatus[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TAppointmentStatus[]>>(
      "/Dropdown/AppointmentStatuses",
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Appointment statuses fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Appointment statuses fetch failed"
          : "Appointment statuses fetch failed",
    };
  }
};

export const getPositions = async (
  search?: string,
): Promise<TResponseServerAction<string[]>> => {
  try {
    const xApiKey = await getCookieByName("x-api-key");
    const response = await api.get<TGenericResponse<string[]>>(
      `/positions/search`,
      {
        params: {
          search,
        },
        headers: {
          Authorization: xApiKey,
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Positions fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Positions fetch failed"
          : "Positions fetch failed",
    };
  }
};

export const getCompanySizes = async (): Promise<
  TResponseServerAction<TCompanySize[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TPosition[]>>(
      "/Dropdown/CompanySizes",
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    return {
      status: "success",
      message: "Company sizes fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Company sizes fetch failed"
          : "Company sizes fetch failed",
    };
  }
};

export const getAvailableProjectFileTypes = async (): Promise<
  TResponseServerAction<{ value: string; text: string }[]>
> => {
  return {
    status: "success",
    message: "Available Project File Types successfully",
    metaData: [
      {
        value: "Image",
        text: "Image",
      },
      {
        value: "Document",
        text: "Document",
      },
      {
        value: "Video",
        text: "Video",
      },
    ],
  };
};

export const getCurrencies = async (): Promise<
  TResponseServerAction<TCurrency[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TCurrency[]>>(
      "/Dropdown/Currencies",
    );

    return {
      status: "success",
      message: "Currencies fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Currencies fetch failed"
          : "Currencies fetch failed",
    };
  }
};

export const getMessageTypes = async (): Promise<
  TResponseServerAction<TMessageType[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TMessageType[]>>(
      "/Dropdown/messagetype",
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    return {
      status: "success",
      message: "Message types fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Message types fetch failed"
          : "Message types fetch failed",
    };
  }
};
